import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import useSelection from '../hooks/useSelection';
import ShareButton from '../components/ShareButton';
import TweetSelectionButton from '../components/TweetSelectionButton';
import { highlightText } from '../utils/highlightText';
import ResultContextAssistant from '../components/ResultContextAssistant';
import SEO from '../components/SEO';

const PageContainer = styled.div`
  padding: 4rem 3rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
`;

const SearchBox = styled.div`
  background: #FFFEF9;
  border: 1.5px solid #C4B5A0;
  padding: 3rem 3rem 3rem 3rem;
  box-shadow: 
    inset 0 1px 3px rgba(0, 0, 0, 0.05),
    1px 1px 5px rgba(0, 0, 0, 0.08);
  max-width: 800px;
  margin: 0 auto;
  border-radius: 2px;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
    border-radius: 0;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 1.2rem;
  border: 1.5px solid #C4B5A0;
  background: #FFFFFF;
  font-family: 'Courier New', monospace;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.ink};
  border-radius: 2px;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &::placeholder {
    color: #A89B88;
    font-style: italic;
  }
  
  &:focus {
    outline: none;
    border-color: #A89B88;
    box-shadow: 0 0 0 3px rgba(196, 181, 160, 0.2);
    background: #FFFEF9;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.9rem;
    font-size: 0.95rem;
  }
`;

const ExampleChip = styled.button`
  background: #F0E8D8;
  border: 1px solid #C4B5A0;
  padding: 0.5rem 1rem;
  margin: 0.5rem 0.5rem 0.5rem 0;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.ink};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #E8DCC8;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    padding: 0.4rem 0.75rem;
    font-size: 0.75rem;
    margin: 0.4rem 0.4rem 0.4rem 0;
  }
`;

const ExamplesContainer = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px dashed #D4CFC4;
  display: flex;
  flex-wrap: wrap;
  gap: 0;

  @media (max-width: 480px) {
    margin-top: 1rem;
    padding-top: 1rem;
  }
`;

const ExampleLabel = styled.p`
  font-size: 0.9rem;
  color: #8A7E70;
  margin: 0 0 0.75rem 0;
  font-style: italic;
  width: 100%;

  @media (max-width: 480px) {
    font-size: 0.8rem;
    margin: 0 0 0.5rem 0;
  }
`;

const SearchTitle = styled.h2`
  font-size: 1.3rem;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.colors.ink};
  text-align: center;
  font-weight: normal;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 1rem;
    letter-spacing: 0.5px;
  }
`;

const SearchButton = styled.button`
  padding: 1.2rem 2.5rem;
  background: #C4B5A0;
  color: #FFFEF9;
  border: 1.5px solid #A89B88;
  font-family: 'Courier New', monospace;
  font-weight: bold;
  font-size: 1rem;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 1.5rem;
  border-radius: 2px;
  
  &:hover:not(:disabled) {
    background: #A89B88;
    transform: translateY(-1px);
    box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.12);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 1rem 2rem;
    font-size: 0.95rem;
    margin-top: 1.25rem;
  }

  @media (max-width: 480px) {
    padding: 0.9rem 1.5rem;
    font-size: 0.9rem;
    margin-top: 1rem;
  }
`;

const SearchInputContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ResultsContainer = styled.div`
  margin-top: 3rem;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    margin-top: 2rem;
  }

  @media (max-width: 480px) {
    margin-top: 1.5rem;
  }
`;

const ResultsTitle = styled.h3`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.ink};
  margin-bottom: 1.5rem;
  font-weight: normal;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 1rem;
    letter-spacing: 0.5px;
  }
`;

const ResultCard = styled.div`
  position: relative;
  background: #FFFEF9;
  border: 1.5px solid #C4B5A0;
  padding: 2rem;
  padding-bottom: 4rem;
  margin-bottom: 1.5rem;
  box-shadow: 
    inset 0 1px 3px rgba(0, 0, 0, 0.05),
    1px 1px 5px rgba(0, 0, 0, 0.08);
  border-radius: 2px;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    box-shadow: 
      inset 0 1px 3px rgba(0, 0, 0, 0.05),
      2px 2px 8px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    padding-bottom: 3.5rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    padding-bottom: 3rem;
    margin-bottom: 1rem;
    border-radius: 0;
  }
`;

const ResultHeader = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px dashed #D4CFC4;
  gap: 1rem;
  position: relative;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const FilePath = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.ink};
  font-weight: bold;
  flex: 1;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const MatchBarContainer = styled.div`
  width: 100px;
  height: 8px;
  background-color: #E0E0E0;
  border-radius: 4px;
  overflow: hidden;
  
  /* Desktop: Position at bottom left of card */
  position: absolute;
  bottom: 1.5rem;
  left: 2rem;

  @media (max-width: 768px) {
    position: static;
    margin-left: 1rem;
  }

  @media (max-width: 480px) {
    margin-left: 0;
    margin-top: 0.5rem;
  }
`;

const MatchBarFill = styled.div`
  height: 100%;
  width: ${props => props.width}%;
  background-color: ${props => props.color};
  transition: width 0.3s ease;
`;

const getMatchColor = (score) => {
  if (score >= 0.7) return '#4E7942'; // Muted green
  if (score >= 0.4) return '#D4A04C'; // Muted gold/yellow
  return '#C64D42'; // Muted red
};

const ResultContent = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 0.95rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.ink};

  @media (max-width: 768px) {
    font-size: 0.9rem;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    line-height: 1.4;
  }
`;

const ResultSection = styled.div`
  margin-bottom: 1rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
  font-family: 'Courier New', monospace;
  
  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    margin-bottom: 0.85rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 0.75rem;
  }
`;

const SectionLabel = styled.span`
  font-weight: bold;
  color: #8A7E70;
  margin-right: 0.5rem;

  @media (max-width: 480px) {
    display: block;
    margin-bottom: 0.25rem;
    margin-right: 0;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #8A7E70;
  font-family: 'Courier New', monospace;
  font-style: italic;

  @media (max-width: 768px) {
    padding: 2rem;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
    font-size: 0.9rem;
  }
`;

const ErrorMessage = styled.div`
  background: #F0E8D8;
  border: 1.5px solid #D4847C;
  padding: 1.5rem;
  margin-top: 2rem;
  color: #8A3A2A;
  font-family: 'Courier New', monospace;
  border-radius: 2px;
  word-wrap: break-word;
  overflow-wrap: break-word;

  @media (max-width: 768px) {
    padding: 1.25rem;
    margin-top: 1.5rem;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    margin-top: 1rem;
    font-size: 0.9rem;
    border-radius: 0;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 3rem;
  color: #8A7E70;
  font-family: 'Courier New', monospace;
  font-style: italic;

  @media (max-width: 768px) {
    padding: 2rem;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
    font-size: 0.9rem;
  }
`;

const PaginationContainer = styled.div`
  margin-top: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding: 1rem 0;

  @media (max-width: 768px) {
    margin-top: 1.5rem;
    gap: 0.4rem;
  }

  @media (max-width: 480px) {
    margin-top: 1rem;
    gap: 0.3rem;
  }
`;

const PageButton = styled.button`
  background: ${props => props.$active ? '#D4847C' : '#F0E8D8'};
  color: ${props => props.$active ? 'white' : '#3A3A3A'};
  border: 1px solid #C4B5A0;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 40px;
  
  &:hover:not(:disabled) {
    background: ${props => props.$active ? '#C4736C' : '#E8DCC8'};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 0.45rem 0.85rem;
    font-size: 0.85rem;
    min-width: 36px;
  }

  @media (max-width: 480px) {
    padding: 0.4rem 0.7rem;
    font-size: 0.8rem;
    min-width: 32px;
  }
`;

const PageInfo = styled.span`
  font-family: 'Courier New', monospace;
  color: #716859;
  padding: 0 1rem;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    padding: 0 0.75rem;
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    padding: 0 0.5rem;
    font-size: 0.8rem;
  }
`;

const ResultsCount = styled.p`
  font-family: 'Courier New', monospace;
  color: #716859;
  font-size: 1rem;
  margin-bottom: 1.5rem;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
`;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL || 'https://epstein-files-12.s3.us-east-2.amazonaws.com/raw/Seventh_Production/IMAGES';

const SourceDisclaimer = styled.div`
  margin-top: 3rem;
  margin-bottom: 1rem;
  padding-top: 3rem;
  border-top: 1px solid #D4CFC4;
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
  color: #8A7E70;
  text-align: center;
  font-style: italic;
  line-height: 1.5;

  a {
    color: #A89B88;
    text-decoration: underline;
    
    &:hover {
      color: #716859;
    }
  }

  @media (max-width: 480px) {
    font-size: 0.7rem;
    margin-top: 2rem;
    margin-bottom: 0.75rem;
    padding-top: 2rem;
  }
`;

const NoExactMatchDisclaimer = styled.div`
  background: #FFF8F0;
  border: 1px solid #E8DCC8;
  padding: 0.75rem 1rem;
  margin-bottom: 1.5rem;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  color: #8A7E70;
  text-align: center;
  font-style: italic;
  border-radius: 4px;

  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 0.65rem 0.85rem;
  }
`;

const DocumentImage = styled.img`
  width: 100%;
  max-width: 300px;
  height: auto;
  border: 1px solid #C4B5A0;
  margin-bottom: 1rem;
  display: block;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const ImageContainer = styled.div`
  margin-bottom: 1.5rem;
  background: #F9F5EB;
  padding: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px dashed #D4CFC4;
`;

const ResultFlexContainer = styled.div`
  display: flex;
  gap: 2rem;
  align-items: flex-start;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const LeftColumn = styled.div`
  flex: 0 0 300px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex: 1;
    width: 100%;
  }
`;

const RightColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

const FullScreenButton = styled.button`
  background: #C4B5A0;
  color: #FFFEF9;
  border: 1.5px solid #A89B88;
  padding: 0.75rem;
  width: 100%;
  font-family: 'Courier New', monospace;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  font-size: 0.9rem;
  border-radius: 2px;
  
  &:hover {
    background: #A89B88;
    transform: translateY(-1px);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled.div`
  background: #FFFEF9;
  width: 95%;
  max-width: 1400px;
  height: 90vh;
  overflow-y: auto;
  padding: 3rem;
  border-radius: 2px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 2.5rem;
  cursor: pointer;
  color: #8A7E70;
  line-height: 1;
  
  &:hover {
    color: #333;
  }
`;

const ModalImage = styled.img`
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  margin: 0 auto;
  border: 1px solid #C4B5A0;
  background: #fff;
`;

function Search() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { selection, selectionRect, clearSelection } = useSelection();
  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastSearchedQuery, setLastSearchedQuery] = useState('');
  const resultsRef = useRef(null);
  const modalContentRef = useRef(null);
  const RESULTS_PER_PAGE = 10;

  const selectionText = selection?.text?.trim() || '';
  const [selectionFilePath, setSelectionFilePath] = useState(null);

  const isSelectionWithinResults = useMemo(() => {
    if (!selectionText) {
      return false;
    }

    if (typeof window === 'undefined') {
      return false;
    }

    const windowSelection = window.getSelection();
    if (!windowSelection || windowSelection.rangeCount === 0) {
      return false;
    }

    const range = windowSelection.getRangeAt(0);
    if (!range) {
      return false;
    }

    let container = range.commonAncestorContainer;
    while (container && container.nodeType === 3) {
      container = container.parentNode;
    }

    if (!container) {
      return false;
    }

    const isInResults = resultsRef.current?.contains(container);
    const isInModal = modalContentRef.current?.contains(container);
    return !!(isInResults || isInModal);
  }, [selectionText]);

  const showTweetButton = !!selectionText && isSelectionWithinResults;

  useEffect(() => {
    if (!selectionText) {
      setSelectionFilePath(null);
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const windowSelection = window.getSelection();
    if (!windowSelection || windowSelection.rangeCount === 0) {
      setSelectionFilePath(null);
      return;
    }

    const range = windowSelection.getRangeAt(0);
    if (!range) {
      setSelectionFilePath(null);
      return;
    }

    let node = range.commonAncestorContainer;
    while (node && node.nodeType === 3) {
      node = node.parentNode;
    }

    if (!node || !(node instanceof Element)) {
      setSelectionFilePath(null);
      return;
    }

    const owner = node.closest('[data-file-path]');
    if (owner && (resultsRef.current?.contains(owner) || modalContentRef.current?.contains(owner))) {
      setSelectionFilePath(owner.getAttribute('data-file-path'));
    } else {
      setSelectionFilePath(null);
    }
  }, [selectionText, selectionRect]);

  const handleTweetSelection = useCallback(() => {
    if (!selectionText) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const normalizedText = selectionText.replace(/\s+/g, ' ').trim();
    const maxQuoteLength = 240;
    const quote =
      normalizedText.length > maxQuoteLength
        ? `${normalizedText.slice(0, maxQuoteLength - 1)}…`
        : normalizedText;

    const tweetText = `"${quote}"`;
    const tweetUrl = new URL('https://twitter.com/intent/tweet');
    tweetUrl.searchParams.set('text', tweetText);

    const createSlug = (path) => {
      const withoutExt = path.substring(0, path.lastIndexOf('.')) || path;
      return withoutExt.replace(/\//g, '-');
    };

    if (selectionFilePath) {
      const slug = createSlug(selectionFilePath);
      tweetUrl.searchParams.set('url', `${window.location.origin}/document/${slug}`);
    } else {
      const currentUrl = window.location.href;
      if (currentUrl) {
        tweetUrl.searchParams.set('url', currentUrl);
      }
    }

    window.open(tweetUrl.toString(), '_blank', 'noopener,noreferrer');
    clearSelection();
  }, [selectionText, selectionFilePath, clearSelection]);

  const performSearch = useCallback(async (query = null) => {
    const searchTerm = query || searchQuery;
    if (!searchTerm.trim()) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setResults([]);

    try {
      const response = await fetch(`${API_BASE_URL}/spicy_search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: searchTerm.trim(),
          top_n: 10,  // Top 10 spicy results
          candidate_count: 50  // Use top 50 from semantic, all from fuzzy
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(errorData.detail || `Search failed: ${response.statusText}`);
      }

      // Read response as text first to check size
      const responseText = await response.text();
      console.log(`📦 Response size: ${(responseText.length / 1024 / 1024).toFixed(2)} MB`);
      
      const data = JSON.parse(responseText);
      const receivedResults = data.results || [];
      console.log(`📊 Received ${receivedResults.length} results from backend (total_found: ${data.total_found || 'N/A'})`);
      console.log(`📄 First result: ${receivedResults[0]?.file_path || 'N/A'}`);
      console.log(`📄 Last result: ${receivedResults[receivedResults.length - 1]?.file_path || 'N/A'}`);
      
      if (receivedResults.length !== data.total_found) {
        console.warn(`⚠️  Mismatch: received ${receivedResults.length} results but total_found is ${data.total_found}`);
      }
      
      setResults(receivedResults);
      setCurrentPage(1); // Reset to first page on new search
      setLastSearchedQuery(searchTerm); // Track what was just searched
      
      // Update URL with the search query
      setSearchParams({ q: searchTerm });
    } catch (err) {
      // Provide more specific error messages
      let errorMessage = 'An error occurred while searching. Please try again.';
      
      if (err.message === 'Failed to fetch' || err.name === 'TypeError') {
        // Network error - backend is likely not reachable
        errorMessage = `Cannot connect to backend API at ${API_BASE_URL}. `;
        if (API_BASE_URL.includes('localhost')) {
          errorMessage += 'Make sure the backend server is running on port 8000.';
        } else {
          errorMessage += 'The backend server may be down or unreachable.';
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      console.error('Search error:', err);
      console.error('API Base URL:', API_BASE_URL);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, setSearchParams]);

  // Load query from URL parameter and trigger search
  useEffect(() => {
    // Only run if we're actually on the search page (not document detail or other routes)
    if (location.pathname !== '/') {
      return;
    }
    
    const queryParam = searchParams.get('q');
    // Only trigger search if URL param exists AND differs from last searched query
    if (queryParam && queryParam !== lastSearchedQuery) {
      setSearchQuery(queryParam);
      performSearch(queryParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, location.pathname]); // Re-run when URL or path changes

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (selectedResult) setSelectedResult(null);
        if (selectedImage) setSelectedImage(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedResult, selectedImage]);


  const handleSearch = () => {
    performSearch();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      handleSearch();
    }
  };

  const exampleSearches = [
    'flight to island',
    'private jet',
    'little st james',
    'palm beach mansion',
    'mar-a-lago',
    'prince andrew',
    'ghislaine maxwell',
    'victoria\'s secret',
    'modeling agency',
    'virgin islands',
    'massage',
    'trump and bubba',
    'clinton emails',
    'underage girls',
    'recruitment',
    'mossad'
  ];

  const handleExampleClick = (example) => {
    setSearchQuery(example);
    performSearch(example);
  };

  // Pagination logic
  const totalPages = Math.ceil(results.length / RESULTS_PER_PAGE);
  const startIndex = (currentPage - 1) * RESULTS_PER_PAGE;
  const endIndex = startIndex + RESULTS_PER_PAGE;
  const currentResults = results.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <>
      <SEO 
        title={searchQuery ? `${searchQuery} - Search Results` : 'Home'} 
        description="Search the declassified Jeffrey Epstein documents. Explore people, locations, and timelines involved in the case."
      />
      <PageContainer>
        <SearchTitle>Search the files</SearchTitle>
        
        <SearchBox>
          <SearchInputContainer>
            <Input
              type="text"
              placeholder="Enter keywords, names, or topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
            />
            <SearchButton onClick={handleSearch} disabled={isLoading || !searchQuery.trim()}>
              {isLoading ? 'Searching...' : 'Search'}
            </SearchButton>
          </SearchInputContainer>
          
          <ExamplesContainer>
            <ExampleLabel>Try searching for:</ExampleLabel>
            {exampleSearches.map((example, index) => (
              <ExampleChip 
                key={index}
                onClick={() => handleExampleClick(example)}
                disabled={isLoading}
              >
                {example}
              </ExampleChip>
            ))}
          </ExamplesContainer>
          
          <SourceDisclaimer>
            All files from Jeffrey Epstein's personal computer. 
            Source: <a href="https://drive.google.com/drive/folders/1hTNH5woIRio578onLGElkTWofUSWRoH_" target="_blank" rel="noopener noreferrer">House Oversight Committee</a>
          </SourceDisclaimer>
        </SearchBox>

        {isLoading && (
          <LoadingMessage>Searching the archive...</LoadingMessage>
        )}

        {error && (
          <ErrorMessage>
            <strong>Error:</strong> {error}
          </ErrorMessage>
        )}

        {!isLoading && !error && results.length > 0 && (
          <ResultsContainer ref={resultsRef}>
            <ResultsTitle>Found {results.length} result{results.length !== 1 ? 's' : ''}</ResultsTitle>
            <ResultsCount>
              Showing {startIndex + 1}-{Math.min(endIndex, results.length)} of {results.length}
            </ResultsCount>
            
            {/* Show disclaimer if no exact matches found */}
            {lastSearchedQuery && !currentResults.some(result => {
              const searchableLower = `${result.people || ''} ${result.locations || ''} ${result.dates || ''} ${result.summary || ''}`.toLowerCase();
              return searchableLower.includes(lastSearchedQuery.toLowerCase());
            }) && (
              <NoExactMatchDisclaimer>
                No direct results found. Showing similar items.
              </NoExactMatchDisclaimer>
            )}
            
            {currentResults.map((result, index) => {
              const cleanBase = S3_BASE_URL.replace(/\/$/, '');
              const cleanPath = result.file_path.replace(/^\//, '');
              const imageUrl = `${cleanBase}/${cleanPath}`;

              return (
              <ResultCard key={result.file_path || index} data-file-path={result.file_path}>
                <ResultHeader>
                  <FilePath>{result.file_path}</FilePath>
                </ResultHeader>
                
                <ResultContent>
                  <ResultFlexContainer>
                    <LeftColumn>
                      <ImageContainer>
                        <div onClick={() => setSelectedImage(imageUrl)} style={{ cursor: 'pointer' }}>
                          <DocumentImage 
                            src={imageUrl} 
                            alt={`Document page from ${result.file_path}`}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentNode.style.display = 'none';
                            }}
                          />
                        </div>
                      </ImageContainer>
                      <FullScreenButton onClick={() => setSelectedResult({...result, imageUrl})}>
                        View Full Screen
                      </FullScreenButton>
                    </LeftColumn>

                      <RightColumn>
                        <ResultContextAssistant
                          result={result}
                          searchQuery={lastSearchedQuery}
                          apiBaseUrl={API_BASE_URL}
                        />
                        {result.summary && (
                          <ResultSection>
                            <SectionLabel>Summary:</SectionLabel>
                            {highlightText(result.summary, lastSearchedQuery)}
                          </ResultSection>
                        )}
                        {result.people && (
                          <ResultSection>
                            <SectionLabel>People:</SectionLabel>
                            {highlightText(result.people, lastSearchedQuery)}
                          </ResultSection>
                        )}
                        {result.locations && (
                          <ResultSection>
                            <SectionLabel>Locations:</SectionLabel>
                            {highlightText(result.locations, lastSearchedQuery)}
                          </ResultSection>
                        )}
                        {result.dates && (
                          <ResultSection>
                            <SectionLabel>Dates:</SectionLabel>
                            {highlightText(result.dates, lastSearchedQuery)}
                          </ResultSection>
                        )}
                      </RightColumn>
                    </ResultFlexContainer>

                    <ShareButton filePath={result.file_path} />
                  </ResultContent>
                  <MatchBarContainer title={`${(result.similarity * 100).toFixed(1)}% match`}>
                    <MatchBarFill 
                      width={result.similarity * 100} 
                      color={getMatchColor(result.similarity)} 
                    />
                  </MatchBarContainer>
                </ResultCard>
              );
            })}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <PaginationContainer>
                <PageButton
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  ← Prev
                </PageButton>
                
                {getPageNumbers().map((page, idx) => (
                  page === '...' ? (
                    <PageInfo key={`ellipsis-${idx}`}>...</PageInfo>
                  ) : (
                    <PageButton
                      key={page}
                      $active={currentPage === page}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </PageButton>
                  )
                ))}
                
                <PageButton
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next →
                </PageButton>
                
                <PageInfo>
                  Page {currentPage} of {totalPages}
                </PageInfo>
              </PaginationContainer>
            )}
          </ResultsContainer>
        )}

        {!isLoading && !error && results.length === 0 && searchQuery && (
          <NoResults>No results found. Try different keywords or check your search query.</NoResults>
        )}
      </PageContainer>

      <TweetSelectionButton
        selection={selection}
        selectionRect={selectionRect}
        visible={showTweetButton}
        onTweet={handleTweetSelection}
      />

      {selectedResult && (
        <ModalOverlay onClick={() => setSelectedResult(null)}>
          <ModalContent
            ref={modalContentRef}
            data-file-path={selectedResult.file_path}
            onClick={e => e.stopPropagation()}
          >
            <CloseButton onClick={() => setSelectedResult(null)}>×</CloseButton>
            
            <ResultHeader style={{ borderBottom: '2px solid #C4B5A0' }}>
              <FilePath style={{ fontSize: '1.2rem' }}>{selectedResult.file_path}</FilePath>
              <MatchBarContainer 
                title={`${(selectedResult.similarity * 100).toFixed(1)}% match`}
                style={{ position: 'static', marginLeft: '1rem', flexShrink: 0 }}
              >
                <MatchBarFill 
                  width={selectedResult.similarity * 100} 
                  color={getMatchColor(selectedResult.similarity)} 
                />
              </MatchBarContainer>
            </ResultHeader>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <div style={{ background: '#F9F5EB', padding: '1rem', display: 'flex', justifyContent: 'center' }}>
                <ModalImage src={selectedResult.imageUrl} alt="Full page document" />
              </div>

              <div style={{ fontFamily: "'Courier New', monospace", fontSize: '0.95rem', lineHeight: '1.6' }}>
                {selectedResult.summary && (
                  <ResultSection>
                    <SectionLabel>Summary:</SectionLabel>
                    {highlightText(selectedResult.summary, lastSearchedQuery)}
                  </ResultSection>
                )}
                {selectedResult.people && (
                  <ResultSection>
                    <SectionLabel>People:</SectionLabel>
                    {highlightText(selectedResult.people, lastSearchedQuery)}
                  </ResultSection>
                )}
                {selectedResult.locations && (
                  <ResultSection>
                    <SectionLabel>Locations:</SectionLabel>
                    {highlightText(selectedResult.locations, lastSearchedQuery)}
                  </ResultSection>
                )}
                {selectedResult.dates && (
                  <ResultSection>
                    <SectionLabel>Dates:</SectionLabel>
                    {highlightText(selectedResult.dates, lastSearchedQuery)}
                  </ResultSection>
                )}
                {selectedResult.ocr_content && (
                  <ResultSection>
                    <SectionLabel>Full Content:</SectionLabel>
                    <div style={{ whiteSpace: 'pre-wrap', marginTop: '1rem', padding: '1rem', background: '#f5f5f5', borderRadius: '4px', fontFamily: "'Courier New', monospace" }}>
                      {highlightText(selectedResult.ocr_content, lastSearchedQuery)}
                    </div>
                  </ResultSection>
                )}
              </div>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}

      {selectedImage && (
        <ModalOverlay onClick={() => setSelectedImage(null)} style={{ background: 'rgba(0, 0, 0, 0.9)', cursor: 'pointer' }}>
          <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CloseButton onClick={(e) => { e.stopPropagation(); setSelectedImage(null); }} style={{ color: '#fff', zIndex: 1001 }}>×</CloseButton>
            <img 
              src={selectedImage} 
              alt="Document full size" 
              style={{ maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain', border: '1px solid #fff', cursor: 'default' }}
              onClick={(e) => e.stopPropagation()} 
            />
          </div>
        </ModalOverlay>
      )}
    </>
  );
}

export default Search;
